import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Spinner, Alert, Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, FormText, Row, Col,
} from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';

const INTERVAL_OPTIONS = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'WeeklyCombinedFromDaily', label: 'Weekly (combined from daily candles)' },
];

// Canonical package-output columns an admin can drop. Mirrors
// CtracPackageService.CanonicalColumnName — unknown values are ignored
// by the server so we restrict the UI to the canonical set to avoid
// silent typos.
const EXCLUDE_COLUMN_OPTIONS = [
  { value: 'OPEN', label: 'OPEN', description: 'Open price' },
  { value: 'HIGH', label: 'HIGH', description: 'High price' },
  { value: 'LOW', label: 'LOW', description: 'Low price' },
  { value: 'CLOSE', label: 'CLOSE', description: 'Close price' },
  { value: 'VOL', label: 'VOL', description: 'Volume (CSV: Volume)' },
  { value: 'OI', label: 'OI', description: 'Open Interest (CTRAC only)' },
];

// Canonical package-output columns an admin can map to a custom name.
// Mirrors EXCLUDE_COLUMN_OPTIONS minus any column that is intentionally
// non-mappable. DATE is always written as "DATE" so it never appears
// here. Unknown values are ignored by the server so we restrict the UI
// to the canonical set to avoid silent typos.
const MAPPABLE_COLUMN_OPTIONS = EXCLUDE_COLUMN_OPTIONS;

// Canonical pre-scaling for the raw candle Volume before it is written
// into the package. The options match the values the package builder
// recognises — null is sent as the JSON null so the server keeps the
// pre-existing "do not divide" behaviour. Any other value (e.g. 0, or
// a negative) is normalised to null on the server so a stale row
// cannot crash the build.
const VOLUME_DIVISOR_OPTIONS = [
  // The first option is the "no division" sentinel. The value is the
  // string 'null' — when the form is submitted the JSON.stringify step
  // emits a literal JSON null, which the controller already treats as
  // "do not divide".
  { value: null, label: 'As is — do not divide' },
  { value: 10, label: '10' },
  { value: 100, label: '100' },
  { value: 1000, label: '1000' },
  { value: 10000, label: '10000' },
];

const formatVolumeDivisor = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return `÷ ${v}`;
};

const emptyForm = () => ({
  Id: 0,
  TickerId: null,
  Ticker: null,
  Name: '',
  Code: '',
  Subfolder: '',
  Interval: 'Daily',
  ExcludeColumns: [],
  // yyyy-MM-dd (HTML5 date input value) or '' when not set.
  StartDate: '',
  // null = "do not divide" — the pre-existing behaviour. Positive
  // integer values are sent as-is; the controller normalises any
  // non-positive value back to null.
  VolumeDivisor: null,
  // Per-row rename map: { canonicalColumn -> alias }. Always keyed by
  // the upper-case canonical column name so the wire format matches
  // what the server expects. Empty object means "use the canonical
  // names" — the pre-existing behaviour.
  ColumnNamesMap: {},
});

// Returns the rows of the column-mapping table, hiding any canonical
// column that the admin has excluded from the package output. We do
// not strip the entry from `form.ColumnNamesMap` so a checkbox toggle
// does not lose an in-progress alias name; the visibility check alone
// is enough to satisfy the "do not show excluded columns in the
// remapping table" requirement.
const visibleMappingColumns = (excludeColumns) => {
  const excluded = new Set((excludeColumns || []).map((c) => String(c).toUpperCase()));
  return MAPPABLE_COLUMN_OPTIONS.filter((opt) => !excluded.has(opt.value));
};

const formatColumnMapping = (map) => {
  if (!map || typeof map !== 'object') return '—';
  const entries = Object.entries(map)
    .filter(([, alias]) => alias !== undefined && alias !== null && String(alias).trim() !== '')
    .map(([canonical, alias]) => `${canonical}→${alias}`);
  if (entries.length === 0) return '—';
  return entries.join(', ');
};

const formatStartDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
};

// Converts the yyyy-MM-dd value the date picker emits into the ISO
// 8601 timestamp the backend expects, with the time portion fixed at
// UTC midnight (e.g. "2026-08-05T00:00Z"). The package builder then
// compares this value against the global TimeFrom without any time-zone
// drift regardless of the admin's local TZ.
const toIsoStartDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return null;
  const d = new Date(`${yyyyMmDd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

// Renders an ISO / DateTime as a yyyy-MM-dd string for the HTML5
// date input. We read the UTC date components so the displayed day
// matches what the backend persisted (the value is always UTC midnight).
const fromIsoToDateInput = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};

const formatDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

export const AdminPackageTickers = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editing, setEditing] = useState(null); // null | 'new' | row
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null); // row | null
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [successMessage, setSuccessMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.listPackageTickers();
      setRows(data || []);
    } catch (err) {
      console.error('Failed to load package tickers', err);
      setError(err.message || 'Failed to load package tickers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ---------- Add / Edit modal -------------------------------------------------

  const openNew = () => {
    setEditing('new');
    setForm(emptyForm());
    setSaveError(null);
    setTickerSearch('');
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      Id: row.Id,
      TickerId: row.TickerId,
      Ticker: row.Ticker || null,
      Name: row.Name || '',
      Code: row.Code || '',
      Subfolder: row.Subfolder || '',
      Interval: row.Interval || 'Daily',
      ExcludeColumns: Array.isArray(row.ExcludeColumns) ? [...row.ExcludeColumns] : [],
      StartDate: fromIsoToDateInput(row.StartDate),
      VolumeDivisor: (row.VolumeDivisor === null || row.VolumeDivisor === undefined)
        ? null
        : row.VolumeDivisor,
      ColumnNamesMap: row.ColumnNamesMap && typeof row.ColumnNamesMap === 'object'
        ? { ...row.ColumnNamesMap }
        : {},
    });
    setSaveError(null);
    setTickerSearch('');
  };

  const closeEdit = () => {
    if (saving) return;
    setEditing(null);
    setSaveError(null);
  };

  // Ticker picker (search as you type)
  const [tickerSearch, setTickerSearch] = useState('');
  const [tickerResults, setTickerResults] = useState([]);
  const [tickerSearching, setTickerSearching] = useState(false);

  useEffect(() => {
    if (!editing) {
      setTickerResults([]);
      return undefined;
    }

    let cancelled = false;
    let timer = null;

    const run = async (term) => {
      try {
        setTickerSearching(true);
        const data = await apiClient.searchTickers({
          Offset: 0,
          Limit: 20,
          Active: true,
          OrderBy: 'CodeAsc',
          SearchTerm: term,
        });
        if (cancelled) return;
        // In edit mode keep the currently selected ticker pinned in the
        // list so it doesn't disappear if the search happens to match
        // it. We do NOT filter out tickers already registered as
        // package tickers — a single ticker can legitimately be mapped
        // to multiple rows when Subfolder / Interval differ.
        const items = editing === 'new'
          ? (data?.Items || [])
          : (data?.Items || []).filter((t) => t.Id !== form.TickerId);
        setTickerResults(items);
      } catch (err) {
        if (!cancelled) console.error('Failed to search tickers', err);
      } finally {
        if (!cancelled) setTickerSearching(false);
      }
    };

    if (tickerSearch.length < 1) {
      setTickerResults([]);
      setTickerSearching(false);
      return () => { cancelled = true; };
    }

    timer = setTimeout(() => { run(tickerSearch); }, 250);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickerSearch, editing]);

  const handlePickTicker = (t) => {
    const tickerSummary = `${t.Code || ''} — ${t.Name || ''}`.trim();
    setForm((prev) => ({
      ...prev,
      TickerId: t.Id,
      Ticker: { Id: t.Id, Source: t.Source, Code: t.Code, Name: t.Name },
      // Auto-populate name / code from the selected ticker unless user already typed something.
      Name: prev.Name && prev.Name.trim().length > 0 ? prev.Name : (t.Name || ''),
      Code: prev.Code && prev.Code.trim().length > 0 ? prev.Code : (t.Code || ''),
    }));
    setTickerSearch(tickerSummary);
    setTickerResults([]);
  };

  // Strips empty / no-op entries from the column-names map so the
  // wire format mirrors what the server will persist. Excluded
  // columns are dropped here as well so a stale alias for a
  // no-longer-present column never reaches the JSONB column.
  const buildColumnNamesMapForBody = (currentForm) => {
    const excluded = new Set((currentForm.ExcludeColumns || []).map((c) => String(c).toUpperCase()));
    const result = {};
    const source = currentForm.ColumnNamesMap || {};
    Object.entries(source).forEach(([canonical, alias]) => {
      const canonicalKey = String(canonical).toUpperCase();
      if (excluded.has(canonicalKey)) return;
      const trimmed = (alias || '').trim();
      if (!trimmed) return;
      if (trimmed.toUpperCase() === canonicalKey) return;
      result[canonicalKey] = trimmed;
    });
    return result;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!form.TickerId) {
      setSaveError('Pick a ticker first.');
      return;
    }
    if (!form.Name || !form.Name.trim()) {
      setSaveError('Name is required.');
      return;
    }
    if (!form.Code || !form.Code.trim()) {
      setSaveError('Code is required.');
      return;
    }

    if (!form.Subfolder || !form.Subfolder.trim()) {
      setSaveError('Subfolder is required.');
      return;
    }

    const body = {
      Id: form.Id,
      TickerId: form.TickerId,
      Name: form.Name.trim(),
      Code: form.Code.trim(),
      Subfolder: form.Subfolder.trim(),
      Interval: form.Interval,
      ExcludeColumns: Array.isArray(form.ExcludeColumns) ? form.ExcludeColumns : [],
      // Optional per-ticker floor — sent as an ISO 8601 timestamp with
      // the time portion fixed at UTC midnight (e.g. "2026-08-05T00:00Z").
      // The controller parses it as UTC so the package builder's
      // later-of comparison is independent of the admin's local TZ. An
      // empty picker value clears the floor.
      StartDate: toIsoStartDate(form.StartDate),
      // Optional per-ticker volume divisor — null = "do not divide"; any
      // positive integer value is sent as-is. The controller normalises
      // any non-positive value back to null so a stale row cannot
      // crash the package build.
      VolumeDivisor: (form.VolumeDivisor === null || form.VolumeDivisor === undefined || form.VolumeDivisor === '')
        ? null
        : Number(form.VolumeDivisor),
      // Optional per-row rename map for the canonical package-output
      // columns. Empty object means "use the canonical names" — the
      // pre-existing behaviour. We strip out any mapping whose alias
      // is empty so the server sees the canonical name (which the
      // package builder will then drop, since it equals the canonical
      // key case-insensitively). Mapping entries for excluded columns
      // are also stripped here so the JSONB never carries stale
      // aliases for columns that have been dropped from the package.
      ColumnNamesMap: buildColumnNamesMapForBody(form),
    };

    try {
      setSaving(true);
      setSaveError(null);

      if (editing === 'new') {
        const saved = await apiClient.savePackageTicker(body);
        setRows((prev) => [...prev, saved]);
        // The backend may have flipped CollectCandles on the underlying
        // ticker and enqueued a backfill job to populate candles from
        // either the latest existing point or 1900-01-01. Surface that
        // side-effect in the success message so the admin isn't left
        // wondering why a fresh row will eventually start producing data.
        let msg = `Package ticker #${saved.Id} (${saved.Code}) created.`;
        if (saved.CollectCandlesSet) {
          const fromDate = saved.CollectionStartedFrom
            ? new Date(saved.CollectionStartedFrom).toISOString().slice(0, 10)
            : '1900-01-01';
          msg += ` CollectCandles was disabled on the underlying ticker and has been enabled; a candle backfill job was enqueued starting from ${fromDate}.`;
        }
        setSuccessMessage(msg);
      } else {
        const saved = await apiClient.savePackageTicker(body);
        setRows((prev) => prev.map((r) => (r.Id === saved.Id ? saved : r)));
        setSuccessMessage(`Package ticker #${saved.Id} (${saved.Code}) updated.`);
      }

      setEditing(null);
    } catch (err) {
      console.error('Failed to save package ticker', err);
      const msg = err?.structuredErrors
        ? err.structuredErrors.map((e) => e.Message).join('; ')
        : (err.message || 'Failed to save package ticker');
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  // ---------- Delete confirm ---------------------------------------------------

  const openDelete = (row) => {
    setConfirmDelete(row);
    setDeleteError(null);
  };
  const closeDelete = () => {
    if (deleting) return;
    setConfirmDelete(null);
    setDeleteError(null);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      setDeleting(true);
      setDeleteError(null);
      await apiClient.deletePackageTicker(confirmDelete.Id);
      setRows((prev) => prev.filter((r) => r.Id !== confirmDelete.Id));
      setSuccessMessage(`Package ticker #${confirmDelete.Id} deleted.`);
      setConfirmDelete(null);
    } catch (err) {
      console.error('Failed to delete package ticker', err);
      const msg = err?.structuredErrors
        ? err.structuredErrors.map((e) => e.Message).join('; ')
        : (err.message || 'Failed to delete package ticker');
      setDeleteError(msg);
    } finally {
      setDeleting(false);
    }
  };

  // ---------- Render -----------------------------------------------------------

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Package Tickers</h2>
        <Button color="primary" onClick={openNew} disabled={loading}>
          + Add Package Ticker
        </Button>
      </div>

      <p className="text-muted small">
        Each row binds a single ticker into every generated data package (CTRAC, CSV,
        SFX exe, …). The row controls the package-visible name, code and sub-folder
        and selects the candle interval strategy used at build time.
      </p>

      <p className="text-muted small">
        Per-row <strong>Exclude columns</strong> control which OHLCV columns are
        dropped from the generated package output. Useful when a downstream consumer
        only needs a subset of fields. Unknown column names are silently ignored at
        build time.
      </p>

      <p className="text-muted small">
        Per-row <strong>Start date</strong> (optional) acts as a per-ticker floor for
        package generation. When set, the package builder reads candle rows starting
        from the later of this date and the global TimeFrom, so it can only narrow the
        package window — never widen it past the global filter.
      </p>

      <p className="text-muted small">
        Per-row <strong>Volume divisor</strong> (optional) pre-scales the raw candle
        <code> Volume</code> value before it is written into the package. Useful when
        the downstream consumer only needs thousands / tens-of-thousands of shares
        and the raw value would overflow the package format's cell width. The
        package builder divides the raw volume by the chosen integer and rounds to
        the nearest integer before serialising.
      </p>

      <p className="text-muted small">
        Per-row <strong>Column names mapping</strong> (optional) renames the
        canonical package-output columns on a per-row basis. Multiple canonical
        columns can be mapped to the same alias (e.g. <code>HIGH</code>,
        <code> LOW</code>, <code>CLOSE</code> all to <code>C</code>). The mapping
        is applied to both the CTRAC DOP file and the CSV header. Columns
        excluded above are hidden from the mapping table because they are not
        written into the package output.
      </p>

      {error && <Alert color="danger" fade={false}>{error}</Alert>}
      {successMessage && (
        <Alert color="success" fade={false} toggle={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {loading ? (
        <div className="text-center p-5">
          <Spinner color="primary" />
          <p className="mt-2">Loading…</p>
        </div>
      ) : (
        <Table striped bordered hover>
          <thead className="table-dark">
            <tr>
              <th style={{ width: '60px' }}>Id</th>
              <th>Ticker</th>
              <th>Name</th>
              <th>Code</th>
              <th>Subfolder</th>
              <th>Interval</th>
              <th>Start date</th>
              <th>Volume divisor</th>
              <th>Exclude columns</th>
              <th>Column mapping</th>
              <th style={{ width: '170px' }}>Updated</th>
              <th style={{ width: '160px' }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="12" className="text-center text-muted py-4">
                  No package tickers configured yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const intervalOption = INTERVAL_OPTIONS.find((o) => o.value === r.Interval);
                const excluded = Array.isArray(r.ExcludeColumns) ? r.ExcludeColumns : [];
                return (
                  <tr key={r.Id}>
                    <td>{r.Id}</td>
                    <td>
                      {r.Ticker && r.Ticker.Source != null ? (
                        <>
                          <div><strong>{r.Ticker.Code}</strong> &nbsp;<span className="text-muted small">({r.Ticker.Source})</span></div>
                          <div className="small text-muted">{r.Ticker.Name}</div>
                        </>
                      ) : (
                        <span className="text-muted">#{r.TickerId}</span>
                      )}
                    </td>
                    <td>{r.Name}</td>
                    <td><code>{r.Code}</code></td>
                    <td><code>{r.Subfolder}</code></td>
                    <td>{intervalOption ? intervalOption.label : r.Interval}</td>
                    <td>{formatStartDate(r.StartDate)}</td>
                    <td>{formatVolumeDivisor(r.VolumeDivisor)}</td>
                    <td>
                      {excluded.length === 0 ? (
                        <span className="text-muted small">—</span>
                      ) : (
                        excluded.map((c) => (
                          <span key={c} className="badge bg-secondary me-1">{c}</span>
                        ))
                      )}
                    </td>
                    <td className="small">{formatColumnMapping(r.ColumnNamesMap)}</td>
                    <td className="small text-muted">{formatDate(r.UpdateTime)}</td>
                    <td>
                      <Button color="primary" size="sm" className="me-1" onClick={() => openEdit(r)}>
                        Edit
                      </Button>
                      <Button color="danger" size="sm" onClick={() => openDelete(r)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      )}

      {/* Add / Edit modal */}
      <Modal isOpen={!!editing} toggle={closeEdit} size="lg" centered>
        <ModalHeader toggle={closeEdit}>
          {editing === 'new' ? 'Add Package Ticker' : `Edit Package Ticker #${form.Id}`}
        </ModalHeader>
        <Form onSubmit={handleSave}>
          <ModalBody>
            {saveError && <Alert color="danger" fade={false}>{saveError}</Alert>}

            <FormGroup>
              <Label for="pt-ticker-search"><strong>Ticker</strong></Label>
              <Input
                id="pt-ticker-search"
                type="text"
                placeholder="Type at least 1 character to search…"
                value={tickerSearch}
                onChange={(e) => {
                  setTickerSearch(e.target.value);
                  // If user changed the search box away from the picked ticker label, clear selection.
                  const prevLabel = `${form.Ticker?.Code || ''} — ${form.Ticker?.Name || ''}`.trim();
                  if (form.TickerId && e.target.value !== prevLabel) {
                    setForm((prev) => ({ ...prev, TickerId: null, Ticker: null }));
                  }
                }}
                autoComplete="off"
                disabled={editing !== 'new'}
              />
              <FormText>
                {editing === 'new'
                  ? 'Pick one of the matching tickers from the list below.'
                  : 'Ticker link cannot be changed here; delete + re-create to re-link.'}
              </FormText>

              {editing === 'new' && tickerSearching && (
                <div className="small text-muted mt-1">Searching…</div>
              )}

              {editing === 'new' && !tickerSearching && tickerResults.length > 0 && (
                <div className="border rounded mt-2" style={{ maxHeight: 240, overflowY: 'auto' }}>
                  <Table size="sm" hover borderless className="mb-0">
                    <tbody>
                      {tickerResults.map((t) => (
                        <tr
                          key={t.Id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handlePickTicker(t)}
                        >
                          <td style={{ width: 90 }}><strong>{t.Code}</strong></td>
                          <td>{t.Name}</td>
                          <td style={{ width: 110 }} className="text-muted small">{t.Source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {editing !== 'new' && form.Ticker && (
                <div className="border rounded p-2 mt-2 bg-light">
                  <div><strong>{form.Ticker.Code}</strong> <span className="text-muted small">({form.Ticker.Source})</span></div>
                  <div className="small">{form.Ticker.Name}</div>
                </div>
              )}
            </FormGroup>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="pt-name"><strong>Name</strong> *</Label>
                  <Input
                    id="pt-name"
                    type="text"
                    value={form.Name}
                    onChange={(e) => setForm((prev) => ({ ...prev, Name: e.target.value }))}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="pt-code"><strong>Code</strong> *</Label>
                  <Input
                    id="pt-code"
                    type="text"
                    value={form.Code}
                    onChange={(e) => setForm((prev) => ({ ...prev, Code: e.target.value }))}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="pt-subfolder"><strong>Subfolder</strong></Label>
                  <Input
                    id="pt-subfolder"
                    type="text"
                    value={form.Subfolder}
                    onChange={(e) => setForm((prev) => ({ ...prev, Subfolder: e.target.value }))}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="pt-interval"><strong>Interval</strong></Label>
                  <Input
                    id="pt-interval"
                    type="select"
                    value={form.Interval}
                    onChange={(e) => setForm((prev) => ({ ...prev, Interval: e.target.value }))}
                  >
                    {INTERVAL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="pt-start-date"><strong>Start date</strong></Label>
                  <Input
                    id="pt-start-date"
                    type="date"
                    value={form.StartDate}
                    onChange={(e) => setForm((prev) => ({ ...prev, StartDate: e.target.value }))}
                  />
                  <FormText>
                    Optional per-ticker floor for package generation. The package
                    builder reads candles starting from the <em>later</em> of this
                    date and the global TimeFrom — so the field can only narrow the
                    package window, never widen it. Leave empty to inherit the
                    global TimeFrom.
                  </FormText>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="pt-volume-divisor"><strong>Volume divisor</strong></Label>
                  <Input
                    id="pt-volume-divisor"
                    type="select"
                    value={form.VolumeDivisor === null || form.VolumeDivisor === undefined ? '' : String(form.VolumeDivisor)}
                    onChange={(e) => {
                      const raw = e.target.value;
                      // The picker emits the literal string 'null' for the
                      // "As is — do not divide" option so the JSON body
                      // round-trips a real JSON null. Any empty / non-matching
                      // value is also coerced to null so a stale row never
                      // carries a non-positive integer.
                      let next = null;
                      if (raw !== '' && raw !== 'null') {
                        const parsed = Number(raw);
                        if (Number.isFinite(parsed) && parsed > 0) {
                          next = parsed;
                        }
                      }
                      setForm((prev) => ({ ...prev, VolumeDivisor: next }));
                    }}
                  >
                    {VOLUME_DIVISOR_OPTIONS.map((o) => (
                      <option key={o.value === null ? 'null' : o.value} value={o.value === null ? '' : o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Input>
                  <FormText>
                    Pre-scales the raw candle <code>Volume</code> before it is
                    written into the package. The package builder divides the raw
                    volume by the chosen integer and rounds to the nearest
                    integer before serialising. Pick <em>As is</em> to write the
                    raw volume unchanged.
                  </FormText>
                </FormGroup>
              </Col>
            </Row>

            <FormGroup>
              <Label><strong>Exclude columns</strong></Label><br/>
              <FormText className="mb-2">
                Tick the columns that should be dropped from the generated package
                output (CTRAC, CSV, SFX exe). <code>DATE</code> is always included.
              </FormText>
              <div className="d-flex flex-wrap" style={{ gap: '0.5rem 1.5rem' }}>
                {EXCLUDE_COLUMN_OPTIONS.map((opt) => {
                  const checked = (form.ExcludeColumns || []).includes(opt.value);
                  return (
                    <FormGroup check key={opt.value} className="mb-0">
                      <Input
                        type="checkbox"
                        id={`pt-exclude-${opt.value}`}
                        checked={checked}
                        onChange={(e) => {
                          setForm((prev) => {
                            const current = Array.isArray(prev.ExcludeColumns) ? prev.ExcludeColumns : [];
                            const next = e.target.checked
                              ? Array.from(new Set([...current, opt.value]))
                              : current.filter((c) => c !== opt.value);
                            return { ...prev, ExcludeColumns: next };
                          });
                        }}
                      />
                      <Label check for={`pt-exclude-${opt.value}`}>
                        <code>{opt.label}</code>
                        <span className="text-muted small ms-1">({opt.description})</span>
                      </Label>
                    </FormGroup>
                  );
                })}
              </div>
              {form.ExcludeColumns && form.ExcludeColumns.length > 0 && (
                <FormText className="mt-2">
                  Active: {form.ExcludeColumns.map((c) => <code key={c} className="me-1">{c}</code>)}
                </FormText>
              )}
            </FormGroup>

            <FormGroup>
              <Label><strong>Column names mapping</strong></Label><br/>
              <FormText className="mb-2">
                Override the name of any canonical package-output column. Useful
                when a downstream consumer expects a different header
                convention (e.g. mapping <code>HIGH</code>, <code>LOW</code>
                and <code>CLOSE</code> all to <code>C</code>). Multiple
                canonical columns can map to the same alias so a single
                output column can receive several inputs. <code>DATE</code>{' '}
                is always written as <code>DATE</code> and cannot be
                remapped.
              </FormText>
              <FormText className="mb-2">
                Excluded columns are hidden from this table — they have no
                header in the package output, so there is nothing to rename.
                Untick the column in <em>Exclude columns</em> above to bring
                its mapping row back.
              </FormText>
              {(() => {
                const visibleCols = visibleMappingColumns(form.ExcludeColumns);
                if (visibleCols.length === 0) {
                  return (
                    <div className="border rounded p-3 bg-light text-muted small">
                      No columns left to map — every canonical column has been
                      excluded above.
                    </div>
                  );
                }
                return (
                  <Table size="sm" bordered className="mb-0">
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: '40%' }}>Canonical</th>
                        <th>Output name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleCols.map((opt) => {
                        const current = (form.ColumnNamesMap || {})[opt.value] || '';
                        return (
                          <tr key={opt.value}>
                            <td>
                              <code>{opt.label}</code>
                              <span className="text-muted small ms-1">({opt.description})</span>
                            </td>
                            <td>
                              <Input
                                type="text"
                                bsSize="sm"
                                placeholder={`${opt.label} (canonical)`}
                                value={current}
                                onChange={(e) => {
                                  const next = e.target.value;
                                  setForm((prev) => {
                                    const updated = { ...(prev.ColumnNamesMap || {}) };
                                    if (!next || !next.trim()) {
                                      delete updated[opt.value];
                                    } else {
                                      updated[opt.value] = next;
                                    }
                                    return { ...prev, ColumnNamesMap: updated };
                                  });
                                }}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                );
              })()}
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={closeEdit} disabled={saving}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={saving || !form.TickerId}>
              {saving ? <><Spinner size="sm" className="me-1" />Saving…</> : 'Save'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Delete confirm */}
      <Modal isOpen={!!confirmDelete} toggle={closeDelete} centered>
        <ModalHeader toggle={closeDelete}>Confirm deletion</ModalHeader>
        <ModalBody>
          {deleteError && <Alert color="danger" fade={false}>{deleteError}</Alert>}
          {confirmDelete && (
            <p>
              Delete package ticker <strong>#{confirmDelete.Id}</strong>{' '}
              (<code>{confirmDelete.Code}</code> – {confirmDelete.Name})?
            </p>
          )}
          <p className="text-muted small mb-0">
            This stops the ticker from being included in any package generated from now
            on. Existing on-disk package archives are not affected.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={closeDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <><Spinner size="sm" className="me-1" />Deleting…</> : 'Delete'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
